import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'pt');

  const article = articles.find((a) => a.slug === slug) || articles[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
  };

  const lang = (currentLang as 'pt' | 'de' | 'en') || 'en';
  const content = article.content[lang] || article.content.en;

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar onLanguageChange={handleLanguageChange} currentLang={currentLang} />

      {/* Article Header */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors mb-6"
          >
            <i className="ri-arrow-left-line"></i>
            {t('nav_home')}
          </Link>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
              {article.tag}
            </span>
            <span className="text-sm text-foreground-500">{article.date}</span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
            {article.title[lang]}
          </h1>

          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
            {article.description[lang]}
          </p>

          <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title[lang]}
              title={article.title[lang]}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 lg:px-8 pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl p-6 md:p-10 lg:p-12 border border-background-200/70">
          <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-8">
            {content.intro}
          </p>

          {content.sections.map((section, idx) => (
            <div key={idx}>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-10 mb-4">
                {section.h2}
              </h2>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                {section.p}
              </p>
            </div>
          ))}

          {/* Safety Note */}
          <div className="bg-accent-50 rounded-xl p-6 md:p-8 my-8 border border-accent-200/50">
            <div className="flex items-start gap-4">
              <i className="ri-shield-check-line text-2xl text-accent-600 mt-1"></i>
              <div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">
                  {t('safety_title')}
                </h3>
                <p className="text-foreground-700">{t('safety_text')}</p>
              </div>
            </div>
          </div>

          <div className="bg-primary-50 rounded-xl p-6 md:p-8 my-8">
            <p className="text-lg text-foreground-800 leading-relaxed mb-4">
              {content.conclusion}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              {content.cta}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* Internal Links */}
          <div className="border-t border-background-200/70 pt-8 mt-8">
            <h3 className="text-xl font-bold text-foreground-900 mb-4">
              {t('related_content')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/"
                className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                  <i className="ri-home-line text-primary-600"></i>
                </div>
                <div>
                  <p className="font-medium text-foreground-900">{t('nav_home')}</p>
                  <p className="text-sm text-foreground-500">{t('back_to_home')}</p>
                </div>
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                  <i className="ri-external-link-line text-primary-600"></i>
                </div>
                <div>
                  <p className="font-medium text-foreground-900">CSSBuy Spreadsheet</p>
                  <p className="text-sm text-foreground-500">{t('visit_main_site')}</p>
                </div>
              </a>
            </div>

            {/* Related articles */}
            <div className="mt-6">
              <p className="text-sm font-semibold text-foreground-900 mb-3">{t('more_articles')}</p>
              <div className="flex flex-wrap gap-2">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 5)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-700 hover:text-primary-600 hover:border-primary-300/50 transition-all whitespace-nowrap"
                    >
                      {a.title[lang]}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}